<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0
-->

<script>
import uiStore from "../utils/UIStore";
import { renderMarkdown } from "../utils/markdownParser";

export let callback = () => console.log("No feedback callback is given");

export let buttonClicked = 0;

export let messageContent = "";

let feedbackMessage = "";

export let feedbackOpen = false;

export let showButtons = true;

let showCheckIcon = false;

let uiTexts;
uiStore.textStore.subscribe((store) => {
	uiTexts = store.texts;
});

function setRating(rating) {
	/*
        Set rating (thumb up, mid, down) and open feedback popup
        */
	buttonClicked = rating;
	feedbackOpen = true;
}

function cancelFeedback() {
	/*
        Reset rating and close feedback popup
        */
	buttonClicked = 0;
	feedbackOpen = false;
}

async function giveFeedback() {
	/*
        Call callback function outside of this component and close feedback popup
        */

	buttonClicked = buttonClicked === 0 ? 2 : buttonClicked;

	callback(buttonClicked, feedbackMessage);
	feedbackOpen = false;
	feedbackMessage = "";
}

function handleEnterKey(event) {
	// start image generation on Enter
	if (event.key === "Enter" && !event.shiftKey && feedbackOpen) {
		giveFeedback();
		event.preventDefault();
	}
}

async function copyToClipboard() {
	const markdownText = messageContent;
	const htmlText = renderMarkdown(markdownText); // Markdown → HTML umwandeln

	// Kopiere HTML in die Zwischenablage
	try {
		await navigator.clipboard.write([
			new ClipboardItem({
				"text/html": new Blob([htmlText], { type: "text/html" }),
				"text/plain": new Blob([markdownText], {
					type: "text/plain",
				}), // Falls HTML nicht unterstützt wird
			}),
		]);
	} catch (err) {
		console.error(uiTexts.feedback.copyError, err);
	}
}
</script>

<svelte:window on:keypress={handleEnterKey} />

{#if showButtons}
    <div class="thumbs_container">
        <button
            on:click={() => {
                copyToClipboard();
                showCheckIcon = true;
                setTimeout(() => (showCheckIcon = false), 3000);
            }}
            title="{uiTexts.feedback.copyTooltip}"
            aria-label="{uiTexts.feedback.copyTooltip}"
        >
            {#if !showCheckIcon}
                <i class="copy-icon icon"></i>
            {:else}
                <i class="check-icon icon"></i>
            {/if}
        </button>
        <button
            title="{uiTexts.feedback.ratingGood}"
            aria-label="{uiTexts.feedback.ratingGood}"
            disabled={buttonClicked !== 0}
            on:click={() => setRating(3)}
            class={buttonClicked === 3 ? "checked" : ""}
            ><i class="thumb-up icon"></i></button
        >
        <button
            title="{uiTexts.feedback.ratingNeutral}"
            aria-label="{uiTexts.feedback.ratingNeutral}"
            disabled={buttonClicked !== 0}
            on:click={() => setRating(2)}
            class={buttonClicked === 2 ? "checked" : ""}
            ><i class="thumb-middle icon"></i></button
        >
        <button
            title="{uiTexts.feedback.ratingBad}"
            aria-label="{uiTexts.feedback.ratingBad}"
            disabled={buttonClicked !== 0}
            on:click={() => setRating(1)}
            class={buttonClicked === 1 ? "checked" : ""}
            ><i class="thumb-down icon"></i></button
        >
    </div>
{/if}

{#if feedbackOpen}
    <div class="background"></div>
    <div class="feedback_container open">
        <div class="button_container">
            {#if !showButtons}
                <div style="display:flex; align-items:center;">
                    <span class="semibold">{uiTexts.feedback.title}</span>
                </div>
            {/if}
            {#if showButtons}
                <div class="thumbs_container">
                    <div
                        class={buttonClicked === 3
                            ? "checked thumb-icon"
                            : "thumb-icon"}
                    >
                        <i class="thumb-up icon"></i>
                    </div>
                    <div
                        class={buttonClicked === 2
                            ? "checked thumb-icon"
                            : "thumb-icon"}
                    >
                        <i class="thumb-middle icon"></i>
                    </div>
                    <div
                        class={buttonClicked === 1
                            ? "checked thumb-icon"
                            : "thumb-icon"}
                    >
                        <i class="thumb-down icon"></i>
                    </div>
                </div>
            {/if}
            <div>
                <button class="btn secondary" on:click={() => cancelFeedback()}
                    >{uiTexts.feedback.cancel}</button
                >
            </div>
        </div>

        <textarea
            type="text"
            id="feedbackText"
            style="background:#fff; margin: 8px 0; height: 100px; width: 100%"
            placeholder={showButtons
                ? uiTexts.feedback.placeholderOptional
                : uiTexts.feedback.placeholderGeneral}
            bind:value={feedbackMessage}
        />
        <div class="save_container">
            <button on:click={() => giveFeedback(feedbackMessage)}
                >{uiTexts.feedback.save}</button
            >
        </div>
    </div>
{/if}

<style>
    .background {
        width: 100%;
        height: 100%;
        position: fixed;
        background-color: rgba(200, 200, 200, 0.8);
        top: 0;
        left: 0;
        z-index: 99;
    }
    .thumb-icon {
        opacity: 0.5;
        margin-right: 8px;
        padding: 4px;
        background-color: var(--bg-color);
        border: none;
        height: 1.75rem;
        width: 1.75rem;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .feedback_container {
        width: 400px;
        z-index: 100;
    }

    .open {
        background-color: var(--bg-color);
        padding: 8px;
        box-shadow: 0px 4px 10px 3px rgba(0, 0, 0, 0.2);
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    .button_container {
        display: flex;
        justify-content: space-between;
    }
    .thumbs_container {
        display: flex;
        align-items: center;
    }

    .thumbs_container button {
        margin-right: 8px;
        padding: 4px;
        background-color: var(--bg-color);
        border: none;
        height: 1.75rem;
        width: 1.75rem;
    }

    .thumbs_container button:active {
        background-color: #999;
    }

    .thumbs_container button:hover {
        background-color: #999;
    }

    .icon {
        background-repeat: no-repeat;
        background-size: contain;
        display: inline-block;
        width: 16px;
        height: 16px;
    }

    .thumbs_container .thumb-up {
        background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="19.543" height="24.637" viewBox="0 0 19.543 24.637"><path id="Pfad_64" data-name="Pfad 64" d="M12.758.072A2.437,2.437,0,0,0,9.767,2.3,17.581,17.581,0,0,1,9.1,6.357a8.521,8.521,0,0,1-1.63,2.569,7.144,7.144,0,0,1-3.34,2.21A2.739,2.739,0,0,0,2,13.666v6.27a2.484,2.484,0,0,0,2.269,2.421A6.717,6.717,0,0,1,7.51,23.491l.075.047a7.71,7.71,0,0,0,1.52.759,6.942,6.942,0,0,0,2.3.34h5.485a3.382,3.382,0,0,0,3.031-1.668,2.915,2.915,0,0,0,.4-1.429,2.185,2.185,0,0,0-.121-.727,5,5,0,0,0,.765-1.412,2.558,2.558,0,0,0,.006-1.8,3.346,3.346,0,0,0,.249-.632,5.176,5.176,0,0,0,0-2.685A3.134,3.134,0,0,0,21,13.716,2.978,2.978,0,0,0,21.368,11a3.248,3.248,0,0,0-1.881-1.994,10.029,10.029,0,0,0-3.943-.331q-.348.031-.694.078a14.732,14.732,0,0,0-.1-7.067A2.163,2.163,0,0,0,13.167.174Zm4.131,23H11.4a5.378,5.378,0,0,1-1.787-.257A6.04,6.04,0,0,1,8.4,22.2l-.063-.038a8.25,8.25,0,0,0-3.9-1.36.921.921,0,0,1-.868-.862V13.666a1.187,1.187,0,0,1,.972-1.019,8.718,8.718,0,0,0,4.1-2.677,10.026,10.026,0,0,0,1.94-3.1A18.943,18.943,0,0,0,11.331,2.4a.87.87,0,0,1,1.045-.812l.411.1a.589.589,0,0,1,.451.4A13.071,13.071,0,0,1,13.012,9.5a.784.784,0,0,0,.933,1.009l0,0,.022,0,.091-.022a14.106,14.106,0,0,1,1.624-.246,8.5,8.5,0,0,1,3.307.257,1.7,1.7,0,0,1,.893,1.019,1.456,1.456,0,0,1-.417,1.6l-.553.553.553.555a1.168,1.168,0,0,1,.241.494,3.592,3.592,0,0,1,0,1.823,1.187,1.187,0,0,1-.241.494l-.553.553.553.555c.074.074.171.277.008.765a3.448,3.448,0,0,1-.791,1.262l-.553.553.553.555a.393.393,0,0,1,.064.266,1.41,1.41,0,0,1-.19.652,1.823,1.823,0,0,1-1.671.878Z" transform="translate(-2 0)"/></svg>');
    }

    .thumbs_container .thumb-middle {
        background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24.637" height="19.543" viewBox="0 0 24.637 19.543"><path id="Pfad_64" data-name="Pfad 64" d="M12.758.072A2.437,2.437,0,0,0,9.767,2.3,17.581,17.581,0,0,1,9.1,6.357a8.521,8.521,0,0,1-1.63,2.569,7.144,7.144,0,0,1-3.34,2.21A2.739,2.739,0,0,0,2,13.666v6.27a2.484,2.484,0,0,0,2.269,2.421A6.717,6.717,0,0,1,7.51,23.491l.075.047a7.71,7.71,0,0,0,1.52.759,6.942,6.942,0,0,0,2.3.34h5.485a3.382,3.382,0,0,0,3.031-1.668,2.915,2.915,0,0,0,.4-1.429,2.185,2.185,0,0,0-.121-.727,5,5,0,0,0,.765-1.412,2.558,2.558,0,0,0,.006-1.8,3.346,3.346,0,0,0,.249-.632,5.176,5.176,0,0,0,0-2.685A3.134,3.134,0,0,0,21,13.716,2.978,2.978,0,0,0,21.368,11a3.248,3.248,0,0,0-1.881-1.994,10.029,10.029,0,0,0-3.943-.331q-.348.031-.694.078a14.732,14.732,0,0,0-.1-7.067A2.163,2.163,0,0,0,13.167.174Zm4.131,23H11.4a5.378,5.378,0,0,1-1.787-.257A6.04,6.04,0,0,1,8.4,22.2l-.063-.038a8.25,8.25,0,0,0-3.9-1.36.921.921,0,0,1-.868-.862V13.666a1.187,1.187,0,0,1,.972-1.019,8.718,8.718,0,0,0,4.1-2.677,10.026,10.026,0,0,0,1.94-3.1A18.943,18.943,0,0,0,11.331,2.4a.87.87,0,0,1,1.045-.812l.411.1a.589.589,0,0,1,.451.4A13.071,13.071,0,0,1,13.012,9.5a.784.784,0,0,0,.933,1.009l0,0,.022,0,.091-.022a14.106,14.106,0,0,1,1.624-.246,8.5,8.5,0,0,1,3.307.257,1.7,1.7,0,0,1,.893,1.019,1.456,1.456,0,0,1-.417,1.6l-.553.553.553.555a1.168,1.168,0,0,1,.241.494,3.592,3.592,0,0,1,0,1.823,1.187,1.187,0,0,1-.241.494l-.553.553.553.555c.074.074.171.277.008.765a3.448,3.448,0,0,1-.791,1.262l-.553.553.553.555a.393.393,0,0,1,.064.266,1.41,1.41,0,0,1-.19.652,1.823,1.823,0,0,1-1.671.878Z" transform="translate(0 21.543) rotate(-90)"/></svg>');
        margin-top: 4px;
    }

    .thumbs_container .thumb-down {
        background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="21.327" height="26.886" viewBox="0 0 21.327 26.886"><path id="Pfad_65" data-name="Pfad 65" d="M11.588,26.807a2.659,2.659,0,0,0,3.263-2.429,19.186,19.186,0,0,1,.732-4.43,9.3,9.3,0,0,1,1.779-2.8,7.8,7.8,0,0,1,3.645-2.412,2.989,2.989,0,0,0,2.321-2.762V5.129a2.71,2.71,0,0,0-2.477-2.642A7.33,7.33,0,0,1,17.314,1.25L17.232,1.2A8.415,8.415,0,0,0,15.573.371,7.576,7.576,0,0,0,13.065,0H7.079A3.691,3.691,0,0,0,3.771,1.82a3.181,3.181,0,0,0-.434,1.56,2.384,2.384,0,0,0,.132.794,5.459,5.459,0,0,0-.835,1.541,2.791,2.791,0,0,0-.007,1.965,3.652,3.652,0,0,0-.272.689,5.648,5.648,0,0,0,0,2.93,3.42,3.42,0,0,0,.236.619,3.249,3.249,0,0,0-.4,2.966,3.545,3.545,0,0,0,2.052,2.176,10.945,10.945,0,0,0,4.3.361q.38-.034.758-.086a16.076,16.076,0,0,0,.106,7.712,2.36,2.36,0,0,0,1.731,1.65ZM7.079,1.709h5.986a5.869,5.869,0,0,1,1.95.28,6.591,6.591,0,0,1,1.327.672l.068.041A9,9,0,0,0,20.67,4.187a1,1,0,0,1,.948.941v6.845a1.3,1.3,0,0,1-1.06,1.112A9.514,9.514,0,0,0,16.086,16a10.941,10.941,0,0,0-2.117,3.383,20.672,20.672,0,0,0-.824,4.874A.949.949,0,0,1,12,25.148l-.448-.113a.642.642,0,0,1-.493-.436,14.263,14.263,0,0,1,.248-8.081.855.855,0,0,0-1.018-1.1l-.005,0-.024.005-.1.024a15.394,15.394,0,0,1-1.772.269,9.277,9.277,0,0,1-3.609-.28,1.855,1.855,0,0,1-.975-1.112,1.589,1.589,0,0,1,.455-1.748l.6-.6-.6-.605A1.274,1.274,0,0,1,4,10.828,3.92,3.92,0,0,1,4,8.839,1.3,1.3,0,0,1,4.264,8.3l.6-.6-.6-.605c-.08-.08-.186-.3-.009-.835a3.763,3.763,0,0,1,.864-1.377l.6-.6-.6-.605a.429.429,0,0,1-.07-.291,1.539,1.539,0,0,1,.207-.711A1.99,1.99,0,0,1,7.079,1.71Z" transform="translate(-2 0)"/></svg>');
    }

    .thumbs_container .checked {
        background-color: #999;
    }

    .save_container {
        display: flex;
        justify-content: right;
    }

    .copy-icon {
        background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="19" height="22" viewBox="0 0 19 22"><path id="Pfad_67" data-name="Pfad 67" d="M16,1H4A2.006,2.006,0,0,0,2,3V17H4V3H16Zm3,4H8A2.006,2.006,0,0,0,6,7V21a2.006,2.006,0,0,0,2,2H19a2.006,2.006,0,0,0,2-2V7A2.006,2.006,0,0,0,19,5Zm0,16H8V7H19Z" transform="translate(-2 -1)"/></svg>');
    }

    .check-icon {
        background-image: url('data:image/svg+xml;utf8, <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"> <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>');
    }
</style>
